import {locale} from "../utils";
import Dialog from "./Dialog";
import {Story} from "inkjs";
import {json, texture} from "p3";
import TextFormat from "./TextFormat";
import common from "../common";

export default class DialogSystem extends PIXI.Container {
    constructor() {
        super();

        this.signals = {
            begin: new P3.utils.MiniSignal(),
            next: new P3.utils.MiniSignal(),
            end: new P3.utils.MiniSignal(),
            introduce: new P3.utils.MiniSignal(),
            assign: new P3.utils.MiniSignal(),
            complete: new P3.utils.MiniSignal(),
            reward: new P3.utils.MiniSignal()
        };

        this._story = null;
        this._dialog = null;
        this._target = null;

        this._currentPathString = "";
        this._previousActor = null;

        this.init();
    }

    init() {
        this._dialog = new Dialog();
        this._dialog.position.set(0, 650);
        this._dialog.visible = false;
        this.addChild(this._dialog);

        P3.input.signals.mouseClick.add(this._onMouseClick, this);
    }

    destroy() {
        P3.utils.destroyAllSignals(this.signals);

        P3.input.signals.mouseClick.add(this._onMouseClick, this);

        super.destroy({ children: true });
    }

    update() {
    }

    /**
     * @param {object} data
     * @param {NPC} target
     * @param {string} [path=undefined]
     * @returns {boolean}
     */
    beginDialog(data, target, path = undefined) {
        this._story = new Story(data);

        this._story.BindExternalFunction("introduce", () => this._onExternalFunctionIntroduce(), false);
        this._story.BindExternalFunction("assign", (name) => this._onExternalFunctionAssign(name), false);
        this._story.BindExternalFunction("complete", (name) => this._onExternalFunctionComplete(name), false);
        this._story.BindExternalFunction("reward", (name) => this._onExternalFunctionReward(name), false);

        this._target = target;
        this._previousActor = null;

        path && this._story.ChoosePathString(path);

        if (!common.saveData.settings.musicMute) {
            P3.audio.playSound("music_new_character_chat");
        }

        if (this.nextDialog()) {
            this.signals.begin.dispatch(path);
            return true;
        }
        return false;
    }

    /**
     * @returns {boolean}
     */
    nextDialog() {
        if (this.isDialogActive) {
            let end = true;

            if (this._story.canContinue) {
                this._currentPathString = this._story.state.currentPathString;
                this._story.Continue();

                if (this._story.currentText.trim().length > 0) {
                    end = false;

                    const actor = this.parseTag("actor");
                    const actorHasChanged = actor !== this._previousActor;
                    this._previousActor = actor;

                    this.showMessage(this._story.currentText, actor, true, actorHasChanged);

                    this.signals.next.dispatch(this._currentPathString);

                    P3.audio.playSound("sfx_ui_speech_bubble_close");

                    if (this._story.currentText.includes("idle_1")) {
                        P3.audio.playSound([
                            "vo_iv_oh_00",
                            "vo_iv_oh_01",
                            "vo_iv_oh_02",
                            "vo_bert_oh_00"
                        ]);
                    } else {
                        P3.audio.playSound([
                            "vo_iv_query_00",
                            "vo_iv_query_01",
                            "vo_iv_query_02",
                            "vo_iv_query_03",
                            "vo_iv_query_04",
                            "vo_iv_query_05"
                        ]);
                    }
                }
            }

            end && this.endDialog();
            return !end;
        }
        return false;
    }

    /**
     * @returns {boolean}
     */
    endDialog() {
        if (this.isDialogActive) {
            this._story = null;

            const path = this._currentPathString;
            this._currentPathString = "";

            const target = this._target;
            this._target = null;

            this._dialog.visible = false;
            this._dialog.setText("", "", PIXI.Texture.EMPTY, false);

            this.signals.end.dispatch(path, target);
            return true;
        }
        return false;
    }

    /**
     * @param {string} text
     * @param {string} [actor=undefined]
     * @param {boolean} [animateText=false]
     * @param {boolean} [animateAvatar=false]
     */
    showMessage(text, actor= undefined, animateText = false, animateAvatar = false) {
        let data, avatarName, avatarTexture = PIXI.Texture.EMPTY;
        if (actor) {
            try {
                data = json(actor);
            } catch {
                console.warn("[" + this.constructor.name + "]", "Actor '" + actor + "' not found");
            }
        }

        P3.audio.playSound("sfx_ui_speech_bubble_appear");
        
        if (data) {
            avatarName = locale(data.character.name);
            avatarTexture = texture(data.character.avatar);
        }

        text = TextFormat.formatText(locale(text, true));

        this._dialog.visible = true;
        this._dialog.setText(text, avatarName, avatarTexture, animateText, animateAvatar);
    }

    /**
     * @param {string} name
     * @returns {string}
     */
    parseTag(name) {
        if (this._story) {
            for (let i = 0, count = this._story.currentTags.length; i < count; ++i) {
                const s = this._story.currentTags[i].replace(/ +/, "").split(":", 2);
                const tag = s[0];
                const value = s[1];
                if (tag === name) {
                    return value.toLowerCase();
                }
            }
        }
        return undefined;
    }

    /**
     * @param {Event} event
     * @private
     */
    _onMouseClick(event) {
        if (this.isDialogActive && this.isDialogReady) {
            this.nextDialog();
        }
    }

    /**
     * @private
     */
    _onExternalFunctionIntroduce() {
        this.signals.introduce.dispatch(this._target);
    }

    /**
     * @param {string} quest
     * @private
     */
    _onExternalFunctionAssign(quest) {
        this.signals.assign.dispatch(quest, this._target);
    }

    /**
     * @param {string} quest
     * @private
     */
    _onExternalFunctionComplete(quest) {
        this.signals.complete.dispatch(quest, this._target);
    }

    /**
     * @param {string} item
     * @private
     */
    _onExternalFunctionReward(item) {
        this.signals.reward.dispatch(item, this._target);
    }

    /**
     * @returns {boolean}
     */
    get isDialogActive() {
        return !!this._story;
    }

    /**
     * @returns {boolean}
     */
    get isDialogReady() {
        return !!this._dialog.isReady;
    }

    /**
     * @returns {NPC}
     */
    get target() {
        return this._target;
    }
}